import glow from '@assets/jackpot-glow.png'
import { useGSAP } from '@gsap/react'
import { useConfigurationStore } from '@stores/configurationStore'
import { useJackpotStore } from '@stores/jackpotStore'
import { SlotContent } from '@utils/Slot'
import gsap from 'gsap'
import React, { useEffect, useRef } from 'react'
import JackpotStatistics from '../common/JackpotStatistics'
import { defaultJackpotLinkConfig, jackpotLinkConfig } from './config'
import JackpotLinkStatisticsItem from './JackpotLinkStatisticsItem'
import { useJackpotLinkMock } from './mock'
import {
    GameUIMessageType,
    JackpotLinkHoldSpinMessage,
    JackpotLinkStonesType,
    JackpotLinkTypes,
    JackpotShowStonePickedMessage,
    ViewportType,
} from 'game-ui-types'

let isUsingMock = false

/**
 * JackpotLinkSection Component
 *
 * Handles rendering of all jackpot link including glow for picked stone.
 */
const JackpotLinkSection: React.FC = () => {
    const viewport = useConfigurationStore((state) => state.viewport)
    // keeping a local copy of viewport since GSAP is async and the store might update differently to when gsap ticks run
    // update local copy using useEffect to listen for changes on viewport
    const localViewport = useRef<ViewportType>(viewport)
    const jackpotLinkSectionConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('JackpotLinkSection').position
    )
    const jackpotLinkLogoConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('JackpotLinkLogo').position
    )
    const jackpotLinkLogoConfigDimensions = useConfigurationStore(
        (state) => state.getComponentConfig('JackpotLinkLogo').dimensions
    )
    const jackpotLinkLogoConfigStyles = useConfigurationStore(
        (state) => state.getComponentConfig('JackpotLinkLogo').styles
    )
    const { images } = defaultJackpotLinkConfig[viewport]
    const isJackpotRoundRunning = useJackpotStore((state) => state.isJackpotRoundRunning)
    const incrementPickedStones = useJackpotStore((state) => state.incrementPickedStones)
    const setIsJackpotLinkHoldSpin = useJackpotStore((state) => state.setIsJackpotLinkHoldSpin)
    const pickedStones = useJackpotStore((state) => state.pickedStones)
    // keeping a local copy of picked stones since GSAP is async and the store might update differently to when gsap ticks run
    const localPickedStones = useRef<{ [key in JackpotLinkStonesType]: number }>({ ...pickedStones })
    const glowElement = useRef<HTMLDivElement>(null)

    const logoStyle = {
        ...jackpotLinkLogoConfigPosition,
        ...jackpotLinkLogoConfigDimensions,
    }

    // TODO: remove mocking when using backend data
    if (!isUsingMock) {
        isUsingMock = true
        useJackpotLinkMock()
    }

    useEffect(() => {
        localViewport.current = viewport
    }, [viewport])

    useEffect(() => {
        localPickedStones.current = pickedStones
    }, [pickedStones])

    const getNextAvailableStonePosition = (type: JackpotLinkStonesType): { x: number; y: number } | undefined => {
        const nextIndex = localPickedStones.current[type]
        const stoneConfig = jackpotLinkConfig[type][localViewport.current].extended.stone

        return stoneConfig.positions && stoneConfig.positions[nextIndex]
    }

    const { contextSafe } = useGSAP(
        () => {
            glowElement.current!.style.opacity = '0'

            const handleShowStonePicked = contextSafe(
                ({ coinType: type, tweensDuration }: { coinType: JackpotLinkStonesType; tweensDuration: number }) => {
                    const nextAvailableStonePosition = getNextAvailableStonePosition(type)

                    if (!nextAvailableStonePosition || !glowElement.current) {
                        return
                    }

                    const stoneConfig = jackpotLinkConfig[type][viewport].extended.stone
                    const stoneDimensionOffset = stoneConfig.dimensions / 2
                    const showPosition = {
                        x: nextAvailableStonePosition.x + stoneDimensionOffset,
                        y: nextAvailableStonePosition.y + stoneDimensionOffset,
                    }

                    gsap.fromTo(
                        glowElement.current,
                        {
                            opacity: 0,
                            scale: 0,
                            ...showPosition,
                            transformOrigin: 'center',
                            xPercent: -50,
                            yPercent: -50,
                        },
                        {
                            opacity: 1,
                            scale: 1,
                            repeat: 1,
                            ...showPosition,
                            duration: tweensDuration,
                            yoyo: true,
                            onRepeat: () => {
                                incrementPickedStones(type)
                                localPickedStones.current[type] = localPickedStones.current[type] + 1
                            },
                        }
                    )
                }
            )

            const handleMessage = (event: MessageEvent) => {
                const rawData = event.data
                const { type } = rawData

                switch (type) {
                    case GameUIMessageType.JACKPOT_SHOW_STONE_PICKED: {
                        const data = rawData as JackpotShowStonePickedMessage

                        handleShowStonePicked(data)
                        break
                    }
                    case GameUIMessageType.JACKPOT_LINK_HOLD_SPIN: {
                        const data = rawData as JackpotLinkHoldSpinMessage

                        setIsJackpotLinkHoldSpin(data.isActive)
                        break
                    }
                }
            }

            window.addEventListener('message', handleMessage, false)

            return () => {
                window.removeEventListener('message', handleMessage, false)
            }
        },
        { scope: glowElement }
    )

    return (
        <>
            <JackpotStatistics
                globalConfigPositions={jackpotLinkSectionConfigPosition}
                config={defaultJackpotLinkConfig}
            >
                <SlotContent slot="top">
                    <JackpotLinkStatisticsItem type={JackpotLinkTypes.GRAND} />
                    <JackpotLinkStatisticsItem type={JackpotLinkTypes.MAJOR} />
                </SlotContent>
                {isJackpotRoundRunning && (
                    <div slot="logo" className={jackpotLinkLogoConfigStyles} style={logoStyle}>
                        <img src={images.logo} alt="Jackpot Link Logo" />
                    </div>
                )}
                <SlotContent slot="bottom">
                    <JackpotLinkStatisticsItem type={JackpotLinkTypes.MINOR} />
                    <JackpotLinkStatisticsItem type={JackpotLinkTypes.MINI} />
                </SlotContent>
            </JackpotStatistics>
            <div ref={glowElement} className="glow fixed top-0 left-0 z-[10]">
                <img src={glow} />
            </div>
        </>
    )
}

export default JackpotLinkSection
